import { create } from 'zustand';

export interface ReservationFormData {
  name: string;
  phone: string;
  email?: string;
  pickupDate?: Date;
  requirements: string;
}

export interface TermsChecked {
  reservationConfirmation: boolean;
  reservationChange: boolean;
  cancellationRefund: boolean;
  usageGuide: boolean;
}

// 예약 제출 상태 타입
export type ReservationStatus = 'idle' | 'submitting' | 'success' | 'error';

// 에러 정보 타입
export interface ReservationError {
  message: string;
  timestamp: Date;
}

interface ReservationState {
  formData: ReservationFormData;
  termsChecked: TermsChecked;
  // 예약 제출 관련 상태
  status: ReservationStatus;
  error: ReservationError | null;

  updateFormData: (data: Partial<ReservationFormData>) => void;
  updateTermsChecked: (terms: Partial<TermsChecked>) => void;
  isFormValid: () => boolean;
  areAllTermsChecked: () => boolean;
  resetReservation: () => void;

  // 예약 제출 상태 관리
  setIdle: () => void;
  setSubmitting: () => void;
  setSuccess: () => void;
  setError: (error: string) => void;
  clearError: () => void;
  isSubmitting: () => boolean;
}

const initialFormData: ReservationFormData = {
  name: '',
  phone: '',
  requirements: '',
};

const initialTermsChecked: TermsChecked = {
  reservationConfirmation: false,
  reservationChange: false,
  cancellationRefund: false,
  usageGuide: false,
};

export const useReservationStore = create<ReservationState>((set, get) => ({
  formData: initialFormData,
  termsChecked: initialTermsChecked,
  status: 'idle',
  error: null,

  updateFormData: data =>
    set(state => ({
      formData: { ...state.formData, ...data },
    })),

  updateTermsChecked: terms =>
    set(state => ({
      termsChecked: { ...state.termsChecked, ...terms },
    })),

  isFormValid: () => {
    const { formData } = get();
    return formData.name.trim() !== '' && formData.phone.trim() !== '' && formData.pickupDate !== undefined;
  },

  areAllTermsChecked: () => {
    const { termsChecked } = get();
    return Object.values(termsChecked).every(Boolean);
  },

  resetReservation: () =>
    set({
      formData: initialFormData,
      termsChecked: initialTermsChecked,
      status: 'idle',
      error: null,
    }),

  // 예약 제출 상태 관리 함수들
  setIdle: () => set({ status: 'idle' }),

  setSubmitting: () => set({ status: 'submitting', error: null }),

  setSuccess: () => set({ status: 'success', error: null }),

  setError: (errorMessage: string) =>
    set({
      status: 'error',
      error: {
        message: errorMessage,
        timestamp: new Date(),
      },
    }),

  clearError: () =>
    set(state => ({
      error: null,
      status: state.status === 'error' ? 'idle' : state.status,
    })),

  isSubmitting: () => get().status === 'submitting',
}));
